import { useState, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { validateFiles, handleDragEvent, MAX_FILE_COUNT } from '@/utils/fileHelpers'
import { useDirectUpload, type UploadedFile } from './useDirectUpload'

/**
 * Combined hook for file upload with direct S3 uploads
 * Handles file selection, validation, drag-and-drop, and automatic S3 upload
 */
export function useFileUploadWithDirectUpload() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    uploadProgress,
    uploadFiles,
    cancelUpload,
    retryUpload,
    isUploading
  } = useDirectUpload()

  // Helper to get file key for matching with upload progress
  const getFileKey = (file: File): string => {
    return `${file.name}-${file.size}-${file.lastModified}`
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    addFiles(files)
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const addFiles = useCallback(async (files: File[]) => {
    // Check if adding these files would exceed the maximum
    const currentCount = selectedFiles.length
    const totalAfterAdding = currentCount + files.length

    if (totalAfterAdding > MAX_FILE_COUNT) {
      const remainingSlots = MAX_FILE_COUNT - currentCount
      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_FILE_COUNT} files allowed`)
        return
      } else {
        toast.error(`Can only add ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} (max ${MAX_FILE_COUNT} total)`)
        files = files.slice(0, remainingSlots)
      }
    }

    const { valid, invalid } = validateFiles(files)

    if (invalid.length > 0) {
      invalid.forEach(({ file, error }) => {
        toast.error(`${file.name}: ${error}`)
      })
    }

    if (valid.length > 0) {
      // Add files to local state
      setSelectedFiles(prev => [...prev, ...valid])

      // Start uploading to S3 immediately
      try {
        const uploaded = await uploadFiles(valid)
        setUploadedFiles(prev => [...prev, ...uploaded])
      } catch (error) {
        console.error('Failed to upload files:', error)
        // Upload errors are already tracked in uploadProgress
      }
    }
  }, [selectedFiles.length, uploadFiles])

  const removeFile = useCallback((index: number) => {
    const fileToRemove = selectedFiles[index]
    const fileKey = getFileKey(fileToRemove)

    // Cancel upload if in progress
    cancelUpload(fileKey)

    // Remove from local state
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))

    // Remove from uploaded files
    setUploadedFiles(prev => {
      const uploadedIndex = prev.findIndex(uf =>
        uf.filename === fileToRemove.name && uf.byteSize === fileToRemove.size
      )
      if (uploadedIndex !== -1) {
        // Revoke blob URL to free memory
        URL.revokeObjectURL(prev[uploadedIndex].blobUrl)
        return prev.filter((_, i) => i !== uploadedIndex)
      }
      return prev
    })
  }, [selectedFiles, cancelUpload])

  const handleDrop = useCallback((e: React.DragEvent) => {
    handleDragEvent(e, addFiles)
    setIsDragging(false)
  }, [addFiles])

  const handleDragOver = (e: React.DragEvent) => {
    handleDragEvent(e)
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    handleDragEvent(e)
    setIsDragging(false)
  }

  const clearFiles = useCallback(() => {
    // Don't revoke blob URLs here - they may still be used in optimistic message previews
    // Browser will garbage collect them when the tab is closed
    setSelectedFiles([])
    setUploadedFiles([])
  }, [])

  const setFiles = (files: File[]) => {
    setSelectedFiles(files)
  }

  // Add a pre-uploaded file (e.g., from asset picker) that already has a signed blob ID
  const addPreUploadedFile = useCallback((asset: {
    name: string
    contentType: string
    byteSize: number
    signedBlobId: string
    previewUrl?: string
  }) => {
    const fakeFile = new File([''], asset.name, { type: asset.contentType })

    setSelectedFiles(prev => [...prev, fakeFile])
    setUploadedFiles(prev => [...prev, {
      signedBlobId: asset.signedBlobId,
      url: asset.previewUrl || '',
      blobUrl: asset.previewUrl || '',
      filename: asset.name,
      contentType: asset.contentType,
      byteSize: asset.byteSize
    }])
  }, [])

  // Check if all files are uploaded successfully
  const allFilesUploaded = selectedFiles.length > 0 &&
    uploadedFiles.length === selectedFiles.length &&
    Array.from(uploadProgress.values()).every(p => p.status === 'completed')

  // Check if any uploads failed
  const hasFailedUploads = Array.from(uploadProgress.values()).some(p => p.status === 'error')

  return {
    selectedFiles,
    uploadedFiles,
    uploadProgress,
    isDragging,
    fileInputRef,
    handleFileSelect,
    addFiles,
    removeFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    clearFiles,
    setFiles,
    isUploading,
    allFilesUploaded,
    hasFailedUploads,
    retryUpload,
    getFileKey,
    addPreUploadedFile
  }
}
